import React, { useState, useEffect } from 'react';
import { Wrapper } from './newsletter.styled';
import { Button, Spinner, Form, Badge, Card } from 'react-bootstrap';
import { NewsletterFilter, NewsletterRecipient } from './newsletter.types';
import {
  getNewsletterRecipientsCount,
  getNewsletterRecipients,
  sendNewsletter,
} from 'helpers/http/common';
import toast from 'react-hot-toast';
import FilterBuilder from './components/FilterBuilder';
import EmailComposer from './components/EmailComposer';
import RecipientPreview from './components/RecipientPreview';
import TemplateManager from './components/TemplateManager';
import NewsletterHistory from './components/NewsletterHistory';
import Tabs from 'components/ui/Tabs';
import { X } from 'lucide-react';

const Newsletter = () => {
  const [filters, setFilters] = useState<NewsletterFilter>({
    userType: 'all',
  });
  const [subject, setSubject] = useState('');
  const [htmlContent, setHtmlContent] = useState('');
  const [recipientCount, setRecipientCount] = useState<number>(0);
  const [recipients, setRecipients] = useState<NewsletterRecipient[]>([]);
  const [loading, setLoading] = useState(false);
  const [sendingTest, setSendingTest] = useState(false);
  const [sending, setSending] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [testEmail, setTestEmail] = useState('');
  const [activeFilters, setActiveFilters] = useState<string[]>([]);
  const [activeTab, setActiveTab] = useState<'composer' | 'history'>(
    'composer'
  );

  // Helper function to check if jobActivity object is empty
  const isJobActivityEmpty = (jobActivity: any) => {
    return (
      !jobActivity?.neverPostedJob &&
      !jobActivity?.neverBidOnJob &&
      (jobActivity?.hasCompletedJobs === null ||
        jobActivity?.hasCompletedJobs === undefined) &&
      (jobActivity?.hasActiveJobs === null ||
        jobActivity?.hasActiveJobs === undefined)
    );
  };

  // Transform filters to match backend expectations
  const transformFiltersForBackend = (filters: NewsletterFilter) => {
    const transformedFilters = { ...filters };

    // Convert plural userType to singular for backend
    if (transformedFilters.userType === 'clients') {
      transformedFilters.userType = 'client' as any;
    } else if (transformedFilters.userType === 'freelancers') {
      transformedFilters.userType = 'freelancer' as any;
    }

    // Clean up empty objects and arrays to avoid backend issues
    if (
      transformedFilters.jobActivity &&
      Object.keys(transformedFilters.jobActivity).length === 0
    ) {
      delete transformedFilters.jobActivity;
    }
    if (
      transformedFilters.countries &&
      transformedFilters.countries.length === 0
    ) {
      delete transformedFilters.countries;
    }
    if (transformedFilters.skills && transformedFilters.skills.length === 0) {
      delete transformedFilters.skills;
    }
    if (
      transformedFilters.categories &&
      transformedFilters.categories.length === 0
    ) {
      delete transformedFilters.categories;
    }
    if (
      transformedFilters.accountStatus &&
      transformedFilters.accountStatus.length === 0
    ) {
      delete transformedFilters.accountStatus;
    }

    return transformedFilters;
  };

  // Fetch recipient count whenever filters change
  useEffect(() => {
    fetchRecipientCount();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  const fetchRecipientCount = async () => {
    try {
      setLoading(true);
      const transformedFilters = transformFiltersForBackend(filters);
      console.log('Fetching recipient count with filters:', transformedFilters);
      const data = await getNewsletterRecipientsCount(transformedFilters);
      console.log('Recipient count response:', data);
      setRecipientCount(data.count || 0);
    } catch (error) {
      console.error('Error fetching recipient count:', error);
      setRecipientCount(0);
    } finally {
      setLoading(false);
    }
  };

  const fetchRecipients = async () => {
    try {
      setLoading(true);
      const transformedFilters = transformFiltersForBackend(filters);
      const data = await getNewsletterRecipients(transformedFilters, 1, 50);
      setRecipients(data.recipients || []);
      setShowPreview(true);
    } catch (error) {
      toast.error('Failed to fetch recipients');
      console.error('Error fetching recipients:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSendTest = async () => {
    if (!subject.trim()) {
      toast.error('Please enter a subject');
      return;
    }
    if (!htmlContent.trim()) {
      toast.error('Please enter email content');
      return;
    }
    if (!testEmail.trim()) {
      toast.error('Please enter a test email address');
      return;
    }

    try {
      setSendingTest(true);
      const transformedFilters = transformFiltersForBackend(filters);
      await sendNewsletter({
        filters: transformedFilters,
        subject,
        htmlContent,
        testMode: true,
        testEmail,
      });
      toast.success(`Test email sent to ${testEmail}`);
    } catch (error: any) {
      toast.error(error?.message || 'Failed to send test email');
    } finally {
      setSendingTest(false);
    }
  };

  const handleSend = async () => {
    if (!subject.trim()) {
      toast.error('Please enter a subject');
      return;
    }
    if (!htmlContent.trim()) {
      toast.error('Please enter email content');
      return;
    }
    if (recipientCount === 0) {
      toast.error('No recipients found with current filters');
      return;
    }

    const confirmed = window.confirm(
      `Are you sure you want to send this newsletter to ${recipientCount} recipients?`
    );
    if (!confirmed) return;

    try {
      setSending(true);
      const transformedFilters = transformFiltersForBackend(filters);
      const result = await sendNewsletter({
        filters: transformedFilters,
        subject,
        htmlContent,
        testMode: false,
      });

      // Handle async response - newsletter is queued and processing in background
      if (result.newsletter_id) {
        toast.success(
          `Newsletter queued! Sending to ${recipientCount} recipients in background.`
        );
        // Reset form immediately - sending happens in background
        setSubject('');
        setHtmlContent('');
        setFilters({ userType: 'all' });

        // Switch to history tab to see progress
        setActiveTab('history');
      } else {
        // Fallback for test mode or other responses
        toast.success(
          `Newsletter sent successfully to ${recipientCount} recipients!`
        );
        setSubject('');
        setHtmlContent('');
        setFilters({ userType: 'all' });
      }
    } catch (error: any) {
      toast.error(error?.message || 'Failed to queue newsletter');
    } finally {
      setSending(false);
    }
  };

  const handleFilterChange = (newFilters: NewsletterFilter) => {
    setFilters(newFilters);
    updateActiveFilters(newFilters);
  };

  const handleSelectTemplate = (
    templateSubject: string,
    templateContent: string
  ) => {
    setSubject(templateSubject);
    setHtmlContent(templateContent);
  };

  const clearAllFilters = () => {
    const resetFilters: NewsletterFilter = { userType: 'all' };
    handleFilterChange(resetFilters);
  };

  const updateActiveFilters = (currentFilters: NewsletterFilter) => {
    const active: string[] = [];

    if (currentFilters.userType !== 'all') {
      active.push(
        `User Type: ${
          currentFilters.userType.charAt(0).toUpperCase() +
          currentFilters.userType.slice(1)
        }`
      );
    }

    if (currentFilters.lastLogin && currentFilters.lastLogin.value > 0) {
      active.push(
        `Last Login: ${
          currentFilters.lastLogin.operator === 'more_than'
            ? 'More than'
            : 'Less than'
        } ${currentFilters.lastLogin.value} ${currentFilters.lastLogin.unit}`
      );
    }

    if (
      currentFilters.registrationDate?.from ||
      currentFilters.registrationDate?.to
    ) {
      active.push('Registration Date Filter');
    }

    if (
      currentFilters.emailVerified !== undefined &&
      currentFilters.emailVerified !== null
    ) {
      active.push(
        `Email: ${currentFilters.emailVerified ? 'Verified' : 'Not Verified'}`
      );
    }

    if (
      currentFilters.profileComplete !== undefined &&
      currentFilters.profileComplete !== null
    ) {
      active.push(
        `Profile: ${currentFilters.profileComplete ? 'Complete' : 'Incomplete'}`
      );
    }

    if (currentFilters.countries && currentFilters.countries.length > 0) {
      active.push(`Countries: ${currentFilters.countries.join(', ')}`);
    }

    if (currentFilters.jobActivity?.neverPostedJob) {
      active.push('Never Posted Job');
    }

    if (currentFilters.jobActivity?.neverBidOnJob) {
      active.push('Never Bid on Job');
    }

    if (
      currentFilters.jobActivity?.hasCompletedJobs !== undefined &&
      currentFilters.jobActivity?.hasCompletedJobs !== null
    ) {
      active.push(
        `${
          currentFilters.jobActivity.hasCompletedJobs ? 'Has' : 'No'
        } Completed Jobs`
      );
    }

    if (
      currentFilters.jobActivity?.hasActiveJobs !== undefined &&
      currentFilters.jobActivity?.hasActiveJobs !== null
    ) {
      active.push(
        `${currentFilters.jobActivity.hasActiveJobs ? 'Has' : 'No'} Active Jobs`
      );
    }

    if (
      currentFilters.jobActivity?.minJobs !== undefined ||
      currentFilters.jobActivity?.maxJobs !== undefined
    ) {
      const min = currentFilters.jobActivity?.minJobs || 0;
      const max = currentFilters.jobActivity?.maxJobs || '∞';
      active.push(`Job Count: ${min} - ${max}`);
    }

    if (currentFilters.skills && currentFilters.skills.length > 0) {
      active.push(`Skills: ${currentFilters.skills.join(', ')}`);
    }

    if (currentFilters.categories && currentFilters.categories.length > 0) {
      active.push(`Categories: ${currentFilters.categories.join(', ')}`);
    }

    if (
      currentFilters.hasPaymentMethod !== undefined &&
      currentFilters.hasPaymentMethod !== null
    ) {
      active.push(
        `${currentFilters.hasPaymentMethod ? 'Has' : 'No'} Payment Method`
      );
    }

    if (
      currentFilters.hasTransaction !== undefined &&
      currentFilters.hasTransaction !== null
    ) {
      active.push(
        `${currentFilters.hasTransaction ? 'Has' : 'No'} Transactions`
      );
    }

    if (
      currentFilters.minTransactionAmount ||
      currentFilters.maxTransactionAmount
    ) {
      const min = currentFilters.minTransactionAmount || 0;
      const max = currentFilters.maxTransactionAmount || '∞';
      active.push(`Transaction Amount: $${min} - $${max}`);
    }

    if (
      currentFilters.hasReviews !== undefined &&
      currentFilters.hasReviews !== null
    ) {
      active.push(`${currentFilters.hasReviews ? 'Has' : 'No'} Reviews`);
    }

    if (
      currentFilters.minRating !== undefined &&
      currentFilters.minRating !== null
    ) {
      active.push(`Min Rating: ${currentFilters.minRating}`);
    }

    if (
      currentFilters.hasDisputes !== undefined &&
      currentFilters.hasDisputes !== null
    ) {
      active.push(`${currentFilters.hasDisputes ? 'Has' : 'No'} Disputes`);
    }

    if (
      currentFilters.accountStatus &&
      currentFilters.accountStatus.length > 0
    ) {
      active.push(`Status: ${currentFilters.accountStatus.join(', ')}`);
    }

    if (
      currentFilters.identityVerified !== undefined &&
      currentFilters.identityVerified !== null
    ) {
      active.push(
        `Identity: ${
          currentFilters.identityVerified ? 'Verified' : 'Not Verified'
        }`
      );
    }

    setActiveFilters(active);
  };

  const removeFilter = (filterText: string) => {
    const newFilters = { ...filters };

    if (filterText.startsWith('User Type:')) {
      newFilters.userType = 'all';
    } else if (filterText.startsWith('Last Login:')) {
      delete newFilters.lastLogin;
    } else if (filterText === 'Registration Date Filter') {
      delete newFilters.registrationDate;
    } else if (filterText.startsWith('Email:')) {
      newFilters.emailVerified = null;
    } else if (filterText.startsWith('Profile:')) {
      newFilters.profileComplete = null;
    } else if (filterText.startsWith('Countries:')) {
      newFilters.countries = [];
    } else if (filterText === 'Never Posted Job') {
      if (newFilters.jobActivity) {
        newFilters.jobActivity.neverPostedJob = false;
        if (isJobActivityEmpty(newFilters.jobActivity)) {
          delete newFilters.jobActivity;
        }
      }
    } else if (filterText === 'Never Bid on Job') {
      if (newFilters.jobActivity) {
        newFilters.jobActivity.neverBidOnJob = false;
        if (isJobActivityEmpty(newFilters.jobActivity)) {
          delete newFilters.jobActivity;
        }
      }
    } else if (filterText.includes('Completed Jobs')) {
      if (newFilters.jobActivity) {
        newFilters.jobActivity.hasCompletedJobs = null;
        if (isJobActivityEmpty(newFilters.jobActivity)) {
          delete newFilters.jobActivity;
        }
      }
    } else if (filterText.includes('Active Jobs')) {
      if (newFilters.jobActivity) {
        newFilters.jobActivity.hasActiveJobs = null;
        if (isJobActivityEmpty(newFilters.jobActivity)) {
          delete newFilters.jobActivity;
        }
      }
    } else if (filterText.includes('Payment Method')) {
      newFilters.hasPaymentMethod = null;
    } else if (filterText.includes('Transactions')) {
      newFilters.hasTransaction = null;
    } else if (filterText.startsWith('Transaction Amount:')) {
      delete newFilters.minTransactionAmount;
      delete newFilters.maxTransactionAmount;
    } else if (filterText.includes('Reviews')) {
      newFilters.hasReviews = null;
    } else if (filterText.startsWith('Min Rating:')) {
      delete newFilters.minRating;
    } else if (filterText.includes('Disputes')) {
      newFilters.hasDisputes = null;
    } else if (filterText.startsWith('Job Count:')) {
      if (newFilters.jobActivity) {
        delete newFilters.jobActivity.minJobs;
        delete newFilters.jobActivity.maxJobs;
        if (isJobActivityEmpty(newFilters.jobActivity)) {
          delete newFilters.jobActivity;
        }
      }
    } else if (filterText.startsWith('Skills:')) {
      newFilters.skills = [];
    } else if (filterText.startsWith('Categories:')) {
      newFilters.categories = [];
    } else if (filterText.startsWith('Status:')) {
      newFilters.accountStatus = [];
    } else if (filterText.startsWith('Identity:')) {
      newFilters.identityVerified = null;
    }

    handleFilterChange(newFilters);
  };

  const NEWSLETTER_TABS = [
    { id: 1, label: 'Composer', key: 'composer' },
    { id: 2, label: 'History', key: 'history' },
  ];

  return (
    <Wrapper>
      <div className="d-flex align-items-center my-3">
        <h1 className="fs-32 fw-700 mb-0">Newsletter</h1>
        {loading && <Spinner animation="border" className="ms-2" size="sm" />}
      </div>

      {/* Tabs */}
      <div className="d-flex align-items-center justify-content-between flex-wrap gap-3 mb-4">
        <Tabs
          tabs={NEWSLETTER_TABS}
          activeTab={activeTab}
          onTabChange={(tab) => setActiveTab(tab as 'composer' | 'history')}
        />
      </div>

      {activeTab === 'history' ? (
        <div className="listing-table p-3 mt-4">
          <NewsletterHistory />
        </div>
      ) : (
        <div
          className="row"
          role="tabpanel"
          id="composer-panel"
          aria-labelledby="composer-tab"
        >
          {/* Left Column - Filters */}
          <div className="col-lg-4 mb-4">
            <Card className="filter-card">
              <Card.Body>
                <div className="d-flex align-items-center justify-content-between mb-3">
                  <h5 className="mb-0 fw-600">Filters</h5>
                  <Badge bg={recipientCount > 0 ? 'success' : 'secondary'}>
                    {loading ? '...' : `${recipientCount} Recipients`}
                  </Badge>
                </div>

                {/* Active Filters */}
                {activeFilters.length > 0 ? (
                  <div className="active-filters mb-3">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <small className="text-muted fw-500">
                        Active Filters ({activeFilters.length})
                      </small>
                      <Button
                        variant="link"
                        size="sm"
                        onClick={clearAllFilters}
                        className="p-0"
                      >
                        Clear All
                      </Button>
                    </div>
                    <div className="d-flex flex-wrap gap-2">
                      {activeFilters.map((filter, index) => (
                        <Badge
                          key={index}
                          bg="primary"
                          className="d-flex align-items-center gap-1"
                        >
                          {filter}
                          <X
                            size={14}
                            style={{ cursor: 'pointer' }}
                            onClick={() => removeFilter(filter)}
                          />
                        </Badge>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="no-filters-message mb-3">
                    <small className="text-muted">
                      <i className="bi bi-info-circle me-1"></i>
                      No active filters - showing all users
                    </small>
                  </div>
                )}

                <FilterBuilder
                  filters={filters}
                  onChange={handleFilterChange}
                />

                <div className="mt-3">
                  <Button
                    variant="outline-primary"
                    className="w-100"
                    onClick={fetchRecipients}
                    disabled={loading || recipientCount === 0}
                  >
                    Preview Recipients
                  </Button>
                </div>
              </Card.Body>
            </Card>
          </div>

          {/* Right Column - Email Composer */}
          <div className="col-lg-8">
            <Card className="composer-card mb-4">
              <Card.Body>
                <div className="d-flex align-items-center justify-content-between mb-3">
                  <h5 className="mb-0 fw-600">Email Content</h5>
                </div>

                <EmailComposer
                  subject={subject}
                  htmlContent={htmlContent}
                  onSubjectChange={setSubject}
                  onContentChange={setHtmlContent}
                />

                {/* Template Manager */}
                <Card className="mt-4 template-card">
                  <Card.Body>
                    <TemplateManager onSelectTemplate={handleSelectTemplate} />
                  </Card.Body>
                </Card>

                {/* Test Send Section */}
                <Card className="mt-4 test-send-card">
                  <Card.Body>
                    <h6 className="mb-3 fw-600">Test Send</h6>
                    <div className="row align-items-end">
                      <div className="col-md-8">
                        <Form.Group>
                          <Form.Label>Test Email Address</Form.Label>
                          <Form.Control
                            type="email"
                            placeholder="your@email.com"
                            value={testEmail}
                            onChange={(e) => setTestEmail(e.target.value)}
                          />
                        </Form.Group>
                      </div>
                      <div className="col-md-4">
                        <Button
                          variant="outline-secondary"
                          className="w-100"
                          onClick={handleSendTest}
                          disabled={sendingTest || !subject || !htmlContent}
                        >
                          {sendingTest ? (
                            <>
                              <Spinner
                                animation="border"
                                size="sm"
                                className="me-2"
                              />
                              Sending...
                            </>
                          ) : (
                            'Send Test'
                          )}
                        </Button>
                      </div>
                    </div>
                  </Card.Body>
                </Card>

                {/* Send Button */}
                <div className="d-flex justify-content-end mt-4">
                  <Button
                    onClick={handleSend}
                    disabled={
                      sending ||
                      recipientCount === 0 ||
                      !subject ||
                      !htmlContent
                    }
                  >
                    {sending ? (
                      <>
                        <Spinner
                          animation="border"
                          size="sm"
                          className="me-2"
                        />
                        Sending to {recipientCount} recipients...
                      </>
                    ) : (
                      <>
                        <i className="bi bi-send me-2"></i>
                        Send Newsletter to {recipientCount} Recipients
                      </>
                    )}
                  </Button>
                </div>
              </Card.Body>
            </Card>
          </div>
        </div>
      )}

      {/* Recipient Preview Modal */}
      {showPreview && (
        <RecipientPreview
          recipients={recipients}
          totalCount={recipientCount}
          onClose={() => setShowPreview(false)}
        />
      )}
    </Wrapper>
  );
};

export default Newsletter;
